import React from 'react';
import Page from '../components/Page';
import {
  Box,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AppColors } from '../utils/utils';
import { HtmlRenderer } from '../components/HtmlRenderer';
import { SectionViewForFeatures } from '../components/SectionViewForFeatures';

const Features: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // e.g. <600px

  const getSectionHeaderView = (text: string) => {
    return (
      <Box
        sx={{
          px: '20px',
          py: '8px',
          background: AppColors.aiGradientTransparent2,
          borderRadius: '20px',
        }}
      >
        <Typography
          fontWeight={500}
          textAlign={'center'}
          sx={{
            background: AppColors.textGradient,
            '-webkit-background-clip': 'text',
            color: 'transparent',
            fontSize: 'clamp(0.74rem, 1.5vw, 0.875rem)',
          }}
        >
          {text}
        </Typography>
      </Box>
    );
  };

  const getSectionView = (
    idx: number,
    label: string,
    title: string,
    features: string[],
    imagePath: string
  ) => {
    const isImageOnRight = idx % 2 !== 0;
    return (
      <Grid
        size={{ xs: 12, md: 5.9 }}
        display={'flex'}
        justifyContent={'center'}
        alignItems={'center'}
        p={3}
      >
        <Box
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'center'}
          alignItems={'center'}
        >
          {getSectionHeaderView(label)}

          <Typography
            component='p'
            sx={{
              fontSize: 'clamp(1.25rem, 2vw, 2rem)',
              fontWeight: 600,
              color: AppColors.textBlackColor,
              textAlign: isMobile ? 'center' : 'start',
              my: 1,
              // fontSize: { xs: '1.3rem', md: '2.125rem' },
              // fontWeight: 'bold',
              // color: AppColors.textBlackColor,
              '& span': {
                background: AppColors.textGradient,
                '-webkit-background-clip': 'text', // Clip the background to the text
                color: 'transparent', // Make the text color transparent to show the gradient
              },
            }}
          >
            {title}
          </Typography>

          {getFeatureListView(features)}

          {getScreenshotView(idx, imagePath, isImageOnRight)}

          {/* <GradientButton sx={{ mt: 2 }} onClick={() => {}}>
            Learn More
          </GradientButton> */}
        </Box>
      </Grid>
    );
  };

  const getScreenshotView = (
    idx: number,
    imagePath: string,
    isImageOnRight: boolean
  ) => {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          mt: 1,
        }}
      >
        {
          <Box
            component='img'
            src={
              isImageOnRight
                ? '/static/images/bg_mobile_right.svg'
                : '/static/images/bg_mobile_left.svg'
            }
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              height: '100%',
              width: '100%',
            }}
          />
        }

        <Box
          component='img'
          src={imagePath}
          width={{ xs: '80%', sm: '80%', md: '80%' }}
          height={'auto'}
          sx={{ position: 'relative' }}
        />
      </Box>
    );
  };

  const getFeatureListView = (list: string[], titleTextColor?: string) => {
    return (
      <Box
        display={'flex'}
        flexDirection={'column'}
        justifyContent={'center'}
        alignItems={'start'}
      >
        {list.map((item, idx) => {
          return (
            <Stack
              display={'flex'}
              direction={'row'}
              alignItems={'start'}
              alignContent={'start'}
              // px={4}
              py={1}
            >
              <Box
                component={'img'}
                src={'/static/images/ic_ai_arrow.svg'}
                height={'24px'}
                width={'24px'}
              />
              <Typography
                // variant='h6'
                sx={{
                  pl: 1,
                  // fontWeight: '500',
                  color: titleTextColor ?? AppColors.grayThreeColor,
                  // textAlign: 'center',
                  // fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
                  fontSize: 'clamp(0.8rem, 1.2vw, 1rem)',
                  '& span': {
                    background: AppColors.aiGradient,
                    '-webkit-background-clip': 'text', // Clip the background to the text
                    color: 'transparent', // Make the text color transparent to show the gradient
                    fontWeight: 'bold',
                  },
                }}
              >
                <HtmlRenderer htmlString={item} />
              </Typography>
            </Stack>
          );
        })}
      </Box>
    );
  };

  return (
    <>
      <Page title={'Features'}>
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            // maxHeight: isPlayIntroVideo ? '100%' : 'auto',
          }}
        >
          <Box
            component='img'
            src='/static/images/ai_gradient_left.svg'
            sx={{
              position: 'absolute',
              left: { xs: -30, md: 0 },
              top: { xs: 0, md: -170 },
              height: { xs: '40%', sm: '30%', md: 'auto' },
              width: { xs: '40%', sm: '30%', md: 'auto' },
            }}
          />

          <Box
            component='img'
            src='/static/images/ai_gradient_right.svg'
            sx={{
              position: 'absolute',
              height: { xs: '60%', sm: '30%', md: 'auto' },
              width: { xs: '60%', sm: '30%', md: 'auto' },
              right: 0,
              bottom: { xs: 0, md: -150 },
            }}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              py: { xs: 2, md: 5 },
              px: { xs: 5, md: 20 },
              position: 'relative',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                py: 8,
                px: 8,
                background: isMobile
                  ? `url('/static/images/bg_features_page_banner_mobile.png')`
                  : `url('/static/images/bg_features_page_banner_desktop.png')`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                borderRadius: '16px',
              }}
            >
              <Typography
                fontWeight={500}
                textAlign={'center'}
                sx={{
                  color: 'white',
                  fontSize: 'clamp(0.74rem, 1.5vw, 0.875rem)',
                  background: AppColors.whiteTransparent,
                  borderRadius: '20px',
                  px: '20px',
                  py: '8px',
                }}
              >
                KnoCard Features
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: '1.3rem', md: '2.125rem' },
                  fontWeight: 'bold',
                  color: 'white',
                  mt: 2,
                }}
              >
                KnoCard Features Premium Fuel For Your Sales
              </Typography>
            </Box>
          </Box>
        </Box>

        <Grid
          container
          sx={{ background: AppColors.aiGradientCrossTransparent }}
        >
          {getSectionView(
            0,
            'Share. Track. Convert',
            'Smart Links',
            [
              'Sales reps instantly share personalized vehicle info via text with a Smart Link.',
              'Captures customer info (name, phone, email, interest) before they leave the lot.',
              'Automatically follows up and tracks every customer interaction',
            ],
            '/static/features_page/img_1_smart_link.png'
          )}
          {getSectionView(
            1,
            'Every Lead in One Place',
            'KnoCard Pipeline',
            [
              'Every prospect is stored and organized in a digital sales pipeline',
              'Managers can track where each customer is in the buying journey',
              'Follow-ups, test drives, and finance applications are all tracked in one place',
            ],
            '/static/features_page/img_2_pipeline.png'
          )}
        </Grid>

        <Grid container>
          {getSectionView(
            2,
            'Show it,  Don’t Tell it',
            'Integrated Media Files',
            [
              'Every prospect is stored and organized in a digital sales pipeline',
              'Every prospect is stored and organized in a digital sales pipeline',
              'Every prospect is stored and organized in a digital sales pipeline',
            ],
            '/static/features_page/img_3_media_files.png'
          )}
          {getSectionView(
            3,
            'Boost Trust Instantly',
            'Google Reviews',
            [
              'Upload vehicle walk-arounds, testimonial videos, service specials, and more',
              'Customers engage with real content directly inside your KnoCard',
              'Tracks views and video watch time in real time',
            ],
            '/static/features_page/img_4_google_reviews.png'
          )}
        </Grid>

        <SectionViewForFeatures
          idx={1}
          label='See It All, Live'
          title='Owners Dashboard'
          subtitle=''
          features={[
            'Real-time view of all activity across your sales team',
            'Track how many Smart Links are shared and how many new prospects are added',
            'Managers get instant insight into performance, follow-ups, and results',
          ]}
          footerText=''
          imagePath={
            isMobile
              ? '/static/features_page/img_owner_dashboard_new.png'
              : // : '/static/features_page/img_5_owners_dashboard.png'
                '/static/features_page/img_owner_dashboard_new_cropped.png'
          }
          imageBGPath='/static/images/bg_owner_dashboard.svg'
          imageAlign={isMobile ? 'center' : 'end'}
          isForFeature={true}
        />

        <Grid container>
          {getSectionView(
            4,
            'Collect Leads Anywhere, Instantly',
            'Lead Capture Forms',
            [
              'Capture names, numbers, and vehicle interest in seconds',
              'Used during walk-ins, phone calls, or events',
              'All leads are auto-loaded into the salesperson’s KnoCard and manager’s dashboard',
            ],
            '/static/features_page/img_6_lead_capture_forms.png'
          )}
          {getSectionView(
            5,
            'Send One Message, Reach Many',
            'Group Messaging',
            [
              'Send out dealership-wide messages instantly',
              'Announce morning meetings, contests, goals, or urgent updates',
              'Every rep receives the message—even if they’re off-site',
            ],
            '/static/features_page/img_7_group_messaging.png'
          )}
        </Grid>

        <Grid
          container
          sx={{ background: AppColors.aiGradientCrossTransparent }}
        >
          {getSectionView(
            6,
            'Who’s available for the UP?',
            'Sales Rep Availability',
            [
              'Faster Lead Assignment = No Missed Ups',
              'Cleaner Reporting & Accountability',
              'Smarter team flow management ',
            ],
            '/static/features_page/img_8_sales_rep_availability.png'
          )}
          {getSectionView(
            7,
            'Share. Track. Convert',
            'Smart Links',
            [
              'Faster Input = No Lost Data',
              'Pipeline Accuracy in Seconds & Real Time Alerts',
              'Built-In Accountability',
            ],
            '/static/features_page/img_9_quick_actions.png'
          )}
        </Grid>

        <Grid container>
          {getSectionView(
            8,
            'From Useless to Unstoppable',
            'Business Card Scanner',
            [
              'Instant contact capture= NO Lost Leads',
              'Automatic pipeline entry, Instantly you can start connecting',
              'Faster Follow-Up = Faster Deals',
            ],
            '/static/features_page/img_10_business_card_scanner.png'
          )}
          {getSectionView(
            9,
            'Collect What Matters',
            'Smart Forms',
            [
              'Create digital forms for service inquiries, trade-in evaluations, or finance pre-approvals',
              'Customers can submit forms on the spot from their phone or your desktop KnoCard',
              'Forms are stored and tracked under each customer in the system',
            ],
            '/static/features_page/img_11_smart_forms.png'
          )}
        </Grid>

        <Grid
          container
          sx={{ background: AppColors.aiGradientCrossTransparent }}
        >
          {getSectionView(
            10,
            'Scan, Capture and Track',
            'QR Code Integration',
            [
              'Customers scan and instantly connect with the rep’s KnoCard',
              'Display a QR code on car windows, desks, or service counters',
              'Great for events, showroom displays, or off-site promotions',
            ],
            '/static/features_page/img_12_qr_code_integrator.png'
          )}
          {getSectionView(
            11,
            'Real-time Leaderboard Insights',
            'Team Ranks',
            [
              'Drives healthy competition by showing exactly who’s performing — and who’s not.',
              'Creates instant accountability with real- time visibility into rep activity and results.',
              'Motivates your team daily by spotlighting top performers and inspiring the rest.',
            ],
            '/static/features_page/img_13_team_ranks.png'
          )}
        </Grid>
      </Page>
    </>
  );
};

export default Features;
